// src/pages/EditCostCard.js
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { create, all } from 'mathjs'
import FormulaBuilderModal from '../components/FormulaBuilderModal'
import './CostCards.css'

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')

// Create a custom mathjs instance
const math = create({}, all)

// Register a user-friendly 'if' function
math.import({
  if(cond, trueVal, falseVal) {
    return cond ? trueVal : falseVal
  }
}, { override: true })

// Month/year pickers same as CreateCostCard
const MONTHS = [
  { value: '',   label: 'Month'     },
  { value: '01', label: 'January'   },
  { value: '02', label: 'February'  },
  { value: '03', label: 'March'     },
  { value: '04', label: 'April'     },
  { value: '05', label: 'May'       },
  { value: '06', label: 'June'      },
  { value: '07', label: 'July'      },
  { value: '08', label: 'August'    },
  { value: '09', label: 'September' },
  { value: '10', label: 'October'   },
  { value: '11', label: 'November'  },
  { value: '12', label: 'December'  }
]
const YEARS = [''].concat(
  Array.from({ length: 2060 - 2000 + 1 }, (_, i) => (2000 + i).toString())
)

export default function EditCostCard() {
  const { id } = useParams()
  const { state } = useLocation()
  const existing = state?.card
  const nav    = useNavigate()

  const [template, setTemplate]             = useState(null)
  const [card, setCard]                     = useState({})
  const [computed, setComputed]             = useState({})
  const [formulas, setFormulas]             = useState({})
  const [editingFormula, setEditingFormula] = useState(null)
  const [loading, setLoading]               = useState(true)
  const [error, setError]                   = useState(null)
  const [actuals, setActuals]               = useState({})
  const [editingDropdownField, setEditingDropdownField] = useState(null);
  const [dropdownOptionEdits, setDropdownOptionEdits] = useState([]);
  const [editingDropdownOptionIdx, setEditingDropdownOptionIdx] = useState(null);
  // Local overrides for dropdown option formulas: { [fieldKey]: { [optionValue]: formula } }
  const [localDropdownOptionOverrides, setLocalDropdownOptionOverrides] = useState({});
  // Variances returned from backend after persisting
  const [serverVariances, setServerVariances] = useState({});
  

  // Handler to open dropdown formula modal
  function openDropdownFormulaModal(field) {
    // Use overrides if present, otherwise template options
    const overrides = localDropdownOptionOverrides[field.key] || {};
    setEditingDropdownField(field.key);
    setDropdownOptionEdits(
      field.options.map(opt => ({
        ...opt,
        formula: overrides[opt.value] !== undefined ? overrides[opt.value] : opt.formula || ''
      }))
    );
    setEditingDropdownOptionIdx(null);
  }

  // Handler to save dropdown option formulas (local override only)
  function saveDropdownOptionFormulas() {
    setLocalDropdownOptionOverrides(prev => {
      const newOverrides = { ...prev };
      newOverrides[editingDropdownField] = {};
      dropdownOptionEdits.forEach(opt => {
        newOverrides[editingDropdownField][opt.value] = opt.formula || '';
      });
      return newOverrides;
    });
    setEditingDropdownField(null);
    setDropdownOptionEdits([]);
    setEditingDropdownOptionIdx(null);
  }

  // Handler to open FormulaBuilderModal for a specific dropdown option
  function openOptionFormulaBuilder(idx) {
    setEditingDropdownOptionIdx(idx);
  }

  // Handler to save formula from FormulaBuilderModal for a dropdown option
  function saveOptionFormula(expr) {
    setDropdownOptionEdits(edits => {
      const newEdits = [...edits];
      newEdits[editingDropdownOptionIdx] = {
        ...newEdits[editingDropdownOptionIdx],
        formula: expr
      };
      return newEdits;
    });
    setEditingDropdownOptionIdx(null);
  }

  useEffect(() => {
    async function load() {
      try {
        // Use existing card from state if available, otherwise fetch
        let data = existing
        if (!data) {
          const resC = await fetch(`${API_BASE}/api/costcards/${id}`)
          if (!resC.ok) throw new Error('Card not found')
          data = await resC.json()
        }

        let tmpl
        if (data.templateKey.startsWith('custom:')) {
          const tid = data.templateKey.split(':')[1]
          const r   = await fetch(`${API_BASE}/api/custom-templates/${tid}`)
          if (!r.ok) throw new Error('Template not found')
          const dt  = await r.json()
          tmpl = { key: data.templateKey, label: dt.name, settings: dt.settings, fields: dt.fields }
        } else {
          const m = await import(`../data/templates/${data.templateKey}.json`)
          tmpl = m.default || m
        }

        setTemplate(tmpl)

        // initialize card + formulas
        const initialRaw = {}
        const initialFrm = {}
        tmpl.fields.forEach(f => {
          initialRaw[f.key] = (f.type === 'select' || f.type === 'dropdown')
            ? f.options?.[0]?.value ?? ''
            : ''
          if (f.type === 'computed') {
            initialFrm[f.key] = f.formula || ''
          }
        })
        if (data?.formulaFields?.length) {
          data.formulaFields.forEach(ff => {
            initialFrm[ff.key] = ff.expression
          })
        }

        // Hydrate template date/period field from periodExact for existing cards
        let nextCard
        if (data) {
          nextCard = { ...initialRaw, ...data }
          try {
            const dateField = tmpl.fields.find(f => f.type === 'date' || f.type === 'period')
            if (dateField && (nextCard[dateField.key] == null || nextCard[dateField.key] === '') && data.periodExact) {
              nextCard[dateField.key] = data.periodExact
            }
          } catch {}
        } else {
          nextCard = initialRaw
        }

        setCard(nextCard)
        setFormulas(initialFrm)
        
        // Initialize actuals from backend data
        console.log('EditCostCard - data from backend:', data)
        console.log('EditCostCard - actuals from backend:', data?.actuals)
        if (data?.actuals) {
          const actualsFromBackend = {}
          Object.entries(data.actuals).forEach(([fieldKey, actualData]) => {
            console.log(`EditCostCard - processing actual for ${fieldKey}:`, actualData)
            if (actualData && actualData.amount != null) {
              actualsFromBackend[fieldKey] = actualData.amount
            }
          })
          console.log('EditCostCard - final actualsFromBackend:', actualsFromBackend)
          setActuals(actualsFromBackend)
        }
        
        // Initialize server variances from backend data
        if (data?.variances) {
          setServerVariances(data.variances)
        }
      } catch (e) {
        console.error(e)
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, existing])

  useEffect(() => {
    if (!template || !Array.isArray(template.fields)) return

    // build our evaluation scope
    let scope = {}
    // 1. Fill scope with all raw values (number, text, etc.) and selected dropdown values
    template.fields.forEach(f => {
      if (['number', 'computed'].includes(f.type)) {
        scope[f.key] = Number(card[f.key]) || 0;
      } else if (f.type === 'dropdown' || f.type === 'select') {
        scope[f.key] = card[f.key]; // temporarily set to value, will update below
      } else {
        scope[f.key] = card[f.key] ?? '';
      }
    });
    // 2. True fixpoint: for each pass, update all dropdowns and computed fields using latest values
    let changed = true, pass = 0;
    while (changed && pass < 15) {
      changed = false;
      // Dropdowns
      template.fields.forEach(f => {
        if (f.type === 'dropdown' || f.type === 'select') {
          const selected = f.options?.find(opt => opt.value === card[f.key]);
          // Use local override if present
          const overrideFormula = localDropdownOptionOverrides?.[f.key]?.[selected?.value];
          const formulaToUse = overrideFormula !== undefined ? overrideFormula : selected?.formula;
          if (selected && formulaToUse && formulaToUse.trim()) {
            try {
              // Coerce referenced fields to numbers where possible
              const evalScope = { ...scope };
              Object.keys(evalScope).forEach(k => {
                if (!isNaN(Number(evalScope[k]))) evalScope[k] = Number(evalScope[k]);
              });
              const prev = scope[f.key];
              const computed = math.evaluate(formulaToUse, evalScope);
              scope[f.key] = computed;
              if (scope[f.key] !== prev) changed = true;
            } catch (e) {
              const prev = scope[f.key];
              scope[f.key] = 0;
              if (scope[f.key] !== prev) changed = true;
            }
          } else if (selected && !isNaN(Number(selected.value))) {
            const prev = scope[f.key];
            scope[f.key] = Number(selected.value);
            if (scope[f.key] !== prev) changed = true;
          } else {
            // fallback: use the value as is
            const prev = scope[f.key];
            scope[f.key] = card[f.key];
            if (scope[f.key] !== prev) changed = true;
          }
        }
      });
      // Computed fields
      template.fields.forEach(f => {
        if (f.type === 'computed' && f.formula && f.formula.trim()) {
          try {
            const evalScope = { ...scope };
            Object.keys(evalScope).forEach(k => {
              if (!isNaN(Number(evalScope[k]))) evalScope[k] = Number(evalScope[k]);
            });
            const prev = scope[f.key];
            scope[f.key] = math.evaluate(f.formula, evalScope);
            if (scope[f.key] !== prev) changed = true;
          } catch (e) {
            const prev = scope[f.key];
            scope[f.key] = 0;
            if (scope[f.key] !== prev) changed = true;
          }
        }
      });
      pass++;
    }

    const curr = card.currency || template.settings?.currency || ''
    const out  = {}

    template.fields.forEach(f => {
      const built  = f.type === 'computed' && (f.formula || '').trim()
      const custom = (formulas[f.key] || '').trim()
      if (built || custom) {
        // substitute placeholders with either literals or scope values
        let expr = (custom || built)
          // If formulas reference {currency}, prefer numeric scope value; fall back to 0
          .replace(/\{currency\}/g, String(scope.currency ?? 0))
          .replace(/\{freightType\}/g, card.freightType || '')
          .replace(/\{(\w+)\}/g, (_, k) => scope[k])

        let v = 0
        try {
          // use our mathjs instance
          v = math.evaluate(expr, scope)
        } catch {
          v = 0
        }
        if (Number.isNaN(v)) v = 0
        out[f.key]   = v
        scope[f.key] = out[f.key]
      }
    })

    setComputed(out)
  }, [card, formulas, template, localDropdownOptionOverrides])

  

  const handleChange     = (k, v) => setCard(c => ({ ...c, [k]: v }))
  const startEditFormula = k       => setEditingFormula(k)

  // —— UPDATED saveFormula —— strips {…} before parse!
  const saveFormula = expr => {
    // remove braces for syntax check
    const clean = expr.replace(/\{(\w+)\}/g, (_, k) => k)
    try {
      math.parse(clean)  // validate on our math instance
      // store the original (with {…})
      setFormulas(f => ({ ...f, [editingFormula]: expr }))
      setEditingFormula(null)
    } catch (err) {
      alert('Formula syntax error: ' + err.message)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!template) return

    const formulaFields = Object.entries(formulas)
      .filter(([, ex]) => ex.trim())
      .map(([key, ex]) => ({
        key,
        label:      template.fields.find(f => f.key === key)?.label || key,
        expression: ex
      }))

    const payload = { ...card, ...computed, formulaFields }
    // Ensure explicit keys are present
    if (card.currencyOfPurchase != null) payload.currencyOfPurchase = card.currencyOfPurchase

    // Normalize and include periodExact on update
    const normalizeDate = (v) => {
      if (!v) return null
      const s = String(v)
      let m = s.match(/^(\d{4})[\/\-](\d{2})[\/\-](\d{2})$/)
      if (m) return `${m[1]}-${m[2]}-${m[3]}`
      m = s.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/)
      if (m) return `${m[3]}-${m[2]}-${m[1]}`
      const d = new Date(s)
      if (!isNaN(d)) return d.toISOString().slice(0,10)
      return null
    }
    // Prefer explicit periodExact key
    const normalizedExplicitPeriod = normalizeDate(card.periodExact)
    if (normalizedExplicitPeriod) {
      payload.periodExact = normalizedExplicitPeriod
    } else {
      // Fallback to first date/period field in template
      const templateDateKey = template?.fields?.find(f => f.type === 'date' || f.type === 'period')?.key
      if (templateDateKey) {
        const normalized = normalizeDate(card[templateDateKey])
        if (normalized) payload.periodExact = normalized
      }
    }

    // Derive exchangeRate from currencyOfPurchase dropdown if possible, overriding stale values when currency changes
    try {
      const currencyField = template.fields.find(ff => ff.key === 'currencyOfPurchase')
        || template.fields.find(ff => ff.key === 'currency' || /currency/i.test(ff.label || ff.key))
      if (currencyField) {
        const selected = (currencyField.options || []).find(opt => {
          const optRaw = (opt.value ?? opt.code ?? opt.key ?? opt.label ?? '').toString().toLowerCase()
          const curRaw = (card[currencyField.key] ?? '').toString().toLowerCase()
          return optRaw === curRaw
        })
        let derived = null
        if (selected) {
          const formulaToUse = selected.formula
          if (formulaToUse && formulaToUse.trim()) {
            const scope = { ...card, ...computed }
            Object.keys(scope).forEach(k => { if (!isNaN(Number(scope[k]))) scope[k] = Number(scope[k]) })
            try { derived = Number(evaluate(formulaToUse, scope)) } catch { derived = null }
          } else if (
            !isNaN(Number(selected.value)) ||
            !isNaN(Number(selected.numericValue)) ||
            !isNaN(Number(selected.rate)) ||
            !isNaN(Number(selected.amount))
          ) {
            const fallbackNum = [selected.value, selected.numericValue, selected.rate, selected.amount]
              .map(v => Number(v))
              .find(v => !isNaN(v))
            if (!isNaN(fallbackNum)) derived = fallbackNum
          }
        }
        if (derived != null && isFinite(derived)) {
          payload.exchangeRate = derived
        } else if (payload.exchangeRate == null && (computed.exchangeRate != null || card.exchangeRate != null)) {
          // fallback to existing if no derivation was possible
          payload.exchangeRate = Number(computed.exchangeRate ?? card.exchangeRate)
        }
      } else if (payload.exchangeRate == null && (computed.exchangeRate != null || card.exchangeRate != null)) {
        payload.exchangeRate = Number(computed.exchangeRate ?? card.exchangeRate)
      }
    } catch {}

    console.debug('EditCostCard save payload', payload)
    const res = await fetch(`${API_BASE}/api/costcards/${id}`, {
      method: 'PUT',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      const t = await res.text().catch(() => '')
      return alert(t || `Save failed (${res.status})`)
    }
    nav('/costcards', { replace: true })
  }

  if (loading) return <p>Loading…</p>
  if (error)   return <p className="error">Error: {error}</p>

  const fmtNum = (v) => {
    const n = Number(v)
    if (!isFinite(n)) return ''
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
  }
  const formatMonthForInput = (v) => {
    if (!v) return ''
    try {
      if (v instanceof Date) return `${v.getUTCFullYear()}-${String(v.getUTCMonth()+1).padStart(2,'0')}`
      const s = String(v)
      // Accept 07-2025 or 2025-07 and normalize to 2025-07
      let m = s.match(/^(\d{2})[-\/](\d{4})$/)
      if (m) return `${m[2]}-${m[1]}`
      m = s.match(/^(\d{4})[-\/](\d{2})$/)
      if (m) return `${m[1]}-${m[2]}`
      const d = new Date(s)
      if (!isNaN(d)) return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}`
    } catch {}
    return ''
  }
  const formatDateForInput = (v) => {
    if (!v) return ''
    try {
      if (v instanceof Date) return v.toISOString().slice(0,10)
      const s = String(v)
      // If full ISO string, slice first 10
      const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/)
      if (isoMatch) return isoMatch[1]
      // Try native parse
      const d = new Date(s)
      if (!isNaN(d)) return d.toISOString().slice(0,10)
    } catch {}
    return ''
  }
  const readPeriodValue = (fieldKey) => {
    // Prefer periodExact if present on the card; otherwise use the field's own key
    if (Object.prototype.hasOwnProperty.call(card, 'periodExact')) return card.periodExact
    return card[fieldKey]
  }
  const computeVariance = (planned, actual) => {
    const p = Number(planned)
    const a = Number(actual)
    if (!isFinite(p) || !isFinite(a)) return null
    return Number((a - p).toFixed(2))
  }

  // Persist an actual value to backend (only when editing an existing card)
  const persistActual = async (fieldKey, amount) => {
    try {
      const cardId = id
      if (!cardId) return // not persisted for new cards
      const url = `${API_BASE}/api/costcards/${encodeURIComponent(cardId)}/actuals/${encodeURIComponent(fieldKey)}`
      const token = localStorage.getItem('token')
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ amount: Number(amount) || 0, currency: card.currency || null })
      })
      if (!res.ok) return
      const data = await res.json()
      setServerVariances(v => ({ ...v, [fieldKey]: data.variance }))
    } catch (_) {}
  }

  return (
    <div className="cost-cards-page">
      <h1>Edit {template?.label || 'Cost Card'}</h1>
      <form onSubmit={handleSubmit} className="cost-card-form">

      {template.fields.map(f => {
        // Patch: treat text fields with options as dropdowns
        const effectiveType = (f.type === 'text' && Array.isArray(f.options) && f.options.length > 0) ? 'dropdown' : f.type;
        const label      = (f.label || f.key).replace(/\{currency\}/g, card.currency || template?.settings?.currency || '')
        const isCustom   = Boolean(formulas[f.key]?.trim())
        const isComputed = effectiveType === 'computed' || isCustom
        const raw        = isComputed
          ? (computed[f.key] ?? 0)
          : (card[f.key] ?? '')
        const [mo, yr]   = String(card[f.key] ?? '-').split('-')

        // Dropdown/select computed value display
        let dropdownComputedValue = '';
        if (effectiveType === 'dropdown' || effectiveType === 'select') {
          const selected = f.options?.find(opt => opt.value === card[f.key]);
          if (selected) {
            // Prefer local override formula if present
            const overrideFormula = localDropdownOptionOverrides?.[f.key]?.[selected?.value];
            const formulaToUse = overrideFormula !== undefined ? overrideFormula : selected?.formula;
            if (formulaToUse && formulaToUse.trim()) {
              try {
                const evalScope = { ...card, ...computed };
                Object.keys(evalScope).forEach(k => {
                  if (!isNaN(Number(evalScope[k]))) evalScope[k] = Number(evalScope[k]);
                });
                dropdownComputedValue = math.evaluate(formulaToUse, evalScope);
              } catch {
                dropdownComputedValue = 0;
              }
            } else if (!isNaN(Number(selected.value))) {
              dropdownComputedValue = Number(selected.value);
            } else {
              dropdownComputedValue = selected.value;
            }
          }
        }

        // Render period/date as a single side-by-side line (no Actual/Variance)
        if (f.key === 'periodExact' || f.key === 'period') {
          const dateValue = String(card[f.key] ?? '')
          return (
            <div className="cc-line" key={f.key}>
              <div className="cc-name">
                {label}
                <button
                  type="button"
                  className="formula-btn"
                  onClick={() => startEditFormula(f.key)}
                  title="Edit custom formula"
                  style={{ marginLeft: 8 }}
                >⚙️</button>
              </div>
              <div className="cc-cell">
                <input
                  type="date"
                  value={formatDateForInput(dateValue)}
                  onChange={e => setCard(c => ({ ...c, periodExact: e.target.value, [f.key]: e.target.value }))}
                  disabled={isComputed}
                  className="cc-readonly"
                />
              </div>
              <div className="cc-cell"></div>
              <div className="cc-cell"></div>
            </div>
          )
        }

        // Always render dropdown/select as dropdown
        if (effectiveType === 'dropdown' || effectiveType === 'select') {
          // Dropdown/select computed value display
          let dropdownComputedValue = '';
          const selected = f.options?.find(opt => opt.value === card[f.key]);
          if (selected) {
            const overrideFormula = localDropdownOptionOverrides?.[f.key]?.[selected?.value];
            const formulaToUse = overrideFormula !== undefined ? overrideFormula : selected?.formula;
            if (formulaToUse && formulaToUse.trim()) {
              try {
                const evalScope = { ...card, ...computed };
                Object.keys(evalScope).forEach(k => {
                  if (!isNaN(Number(evalScope[k]))) evalScope[k] = Number(evalScope[k]);
                });
                dropdownComputedValue = math.evaluate(formulaToUse, evalScope);
              } catch {
                dropdownComputedValue = 0;
              }
            } else if (!isNaN(Number(selected.value))) {
              dropdownComputedValue = Number(selected.value);
            } else {
              dropdownComputedValue = selected.value;
            }
          }
          // Show formula-btn if any option has a formula
          const hasDropdownFormulas = Array.isArray(f.options) && f.options.some(opt => opt.formula && opt.formula.trim());
          return (
            <div className="cc-line" key={f.key}>
              <div className="cc-name">{label}
                {hasDropdownFormulas && (
                  <button
                    type="button"
                    className="formula-btn"
                    onClick={() => openDropdownFormulaModal(f)}
                    title="Edit dropdown option formulas"
                    style={{ marginLeft: 8 }}
                  >⚙️</button>
                )}
              </div>
              <div className="cc-cell">
                <div className="cc-select-value">
                  <select
                    value={card[f.key] || ''}
                    onChange={e => handleChange(f.key, e.target.value)}
                    disabled={isComputed}
                  >
                    <option value="">Select…</option>
                    {Array.isArray(f.options) && f.options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <span className="cc-select-number">{fmtNum(dropdownComputedValue)}</span>
                </div>
              </div>
              <div className="cc-cell">
                <input
                  type="number"
                  value={actuals[f.key] ?? ''}
                  onChange={e => setActuals(a => ({ ...a, [f.key]: e.target.value }))}
                  onBlur={e => persistActual(f.key, e.target.value)}
                  className="cc-input-right"
                  placeholder="What Was Actually Charged"
                />
              </div>
              <div className="cc-cell">
                {(() => {
                  const planned = Number(dropdownComputedValue) || 0
                  const actual  = actuals[f.key]
                  const v = (serverVariances[f.key] != null) ? serverVariances[f.key] : computeVariance(planned, actual)
                  const sign = v == null ? '' : (v > 0 ? '+' : '')
                  const cls = v == null || v === 0 ? 'text-gray-600' : (v > 0 ? 'text-red-600' : 'text-green-600')
                  const val = v == null ? 'Variance' : `${sign}${fmtNum(v)}`
                  const centerClass = v == null ? 'center' : ''
                  return <input className={`cc-readonly variance-cell ${centerClass} ${cls}`} readOnly value={val} />
                })()}
              </div>
            </div>
          );
        }

        // Fallback for unknown types
        if (!["text","number","computed","month"].includes(effectiveType)) {
          return (
            <div className="form-group" key={f.key}>
              <label>{label}</label>
              <div style={{ color: 'red' }}>Unknown field type: {f.type}</div>
            </div>
          );
        }

        const labelLower = String(label).toLowerCase()
        const keyLower   = String(f.key || '').toLowerCase()
        const noActualVariance = (
          ['supplier', 'proforma', 'product name', 'hs code'].some(s => labelLower.includes(s)) ||
          ['suppliername','proformainvoiceno','productname','hscode'].some(s => keyLower.includes(s))
        )
        const showActualVariance = !noActualVariance && (effectiveType === 'number' || effectiveType === 'computed')
        return (
          <div className="cc-line" key={f.key}>
            {/* Name */}
            <div className="cc-name">
              {label}
              <button
                type="button"
                className="formula-btn"
                onClick={() => startEditFormula(f.key)}
                title="Edit custom formula"
                style={{ marginLeft: 8 }}
              >⚙️</button>
            </div>

            {/* Planned (readonly if computed) */}
            <div className="cc-cell">
              {(() => {
                const inputType = isComputed ? 'text' : (effectiveType === 'number' ? 'number' : 'text')
                const displayValue = isComputed ? fmtNum(raw) : raw
                return (
                  <input
                    type={inputType}
                    value={displayValue}
                    onChange={e => handleChange(f.key, e.target.value)}
                    readOnly={isComputed}
                    className={isComputed ? 'cc-readonly' : 'cc-input-right'}
                  />
                )
              })()}
            </div>

            {/* Actual */}
            <div className="cc-cell">
              {showActualVariance ? (
                <input
                  type="number"
                  value={actuals[f.key] ?? ''}
                  onChange={e => setActuals(a => ({ ...a, [f.key]: e.target.value }))}
                  onBlur={e => persistActual(f.key, e.target.value)}
                  className="cc-input-right"
                  placeholder="What Was Actually Charged"
                />
              ) : null}
            </div>

            {/* Variance */}
            <div className="cc-cell">
              {showActualVariance ? (() => {
                if (!(effectiveType === 'number' || effectiveType === 'computed')) return null
                const planned = isComputed ? (computed[f.key] ?? 0) : Number(card[f.key])
                const actual  = actuals[f.key]
                const v = (serverVariances[f.key] != null) ? serverVariances[f.key] : computeVariance(planned, actual)
                const sign = v == null ? '' : (v > 0 ? '+' : '')
                const cls = v == null || v === 0 ? 'text-gray-600' : (v > 0 ? 'text-red-600' : 'text-green-600')
                const val = v == null ? 'Variance' : `${sign}${fmtNum(v)}`
                const centerClass = v == null ? 'center' : ''
                return (
                  <input className={`cc-readonly variance-cell ${centerClass} ${cls}`} readOnly value={val} />
                )
              })() : null}
            </div>
          </div>
        )
      })}

      {editingFormula && (
        <FormulaBuilderModal
          field={editingFormula}
          existingExpr={formulas[editingFormula] || ''}
          fieldsList={template.fields.map(f => f.key)}
          labels={Object.fromEntries(template.fields.map(f => [f.key, f.label]))}
          onSave={saveFormula}
          onCancel={() => setEditingFormula(null)}
        />
      )}

      {editingDropdownField && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Dropdown Option Formulas: {editingDropdownField}</h3>
            {dropdownOptionEdits.map((opt, idx) => (
              <div key={idx} className="form-group">
                <label>{opt.label}</label>
                <div className="formula-input-group">
                  <input
                    type="text"
                    value={opt.formula || ''}
                    onChange={e => {
                      const newEdits = [...dropdownOptionEdits];
                      newEdits[idx] = { ...newEdits[idx], formula: e.target.value };
                      setDropdownOptionEdits(newEdits);
                    }}
                    placeholder="Enter formula (e.g., {field1} * {field2})"
                  />
                  <button
                    type="button"
                    onClick={() => openOptionFormulaBuilder(idx)}
                    className="formula-btn"
                    title="Open formula builder"
                  >
                    ⚙️
                  </button>
                </div>
              </div>
            ))}
            <div className="modal-actions">
              <button type="button" onClick={saveDropdownOptionFormulas}>Save</button>
              <button type="button" onClick={() => {
                setEditingDropdownField(null);
                setDropdownOptionEdits([]);
                setEditingDropdownOptionIdx(null);
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editingDropdownOptionIdx !== null && (
        <FormulaBuilderModal
          field={`${editingDropdownField}_option_${editingDropdownOptionIdx}`}
          existingExpr={dropdownOptionEdits[editingDropdownOptionIdx]?.formula || ''}
          fieldsList={template.fields.map(f => f.key)}
          labels={Object.fromEntries(template.fields.map(f => [f.key, f.label]))}
          onSave={saveOptionFormula}
          onCancel={() => setEditingDropdownOptionIdx(null)}
        />
      )}

      <div className="form-actions">
        <button type="submit">Update</button>
        <button type="button" onClick={() => nav('/costcards', { replace:true })}>
          Cancel
        </button>
      </div>
    </form>
    </div>
  )
}
